import React, { useState } from 'react';
import { motion } from 'framer-motion';

const SearchQuestionsAnalysis = ({ data }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!data) return null;

  const getPriorityColor = (p) => ({ High: '#ef4444', Medium: '#f97316', Low: '#22c55e' }[p] || '#d1d5db');
  const getDifficultyColor = (d) => ({ Hard: '#ef4444', Medium: '#eab308', Easy: '#10b981' }[d] || '#d1d5db');
  const getDirectionIcon = (d) => ({ Rising: '', Stable: '', Declining: '' }[d] || '');
  const getDirectionColor = (d) => ({ Rising: '#10b981', Stable: '#d1d5db', Declining: '#ef4444' }[d] || '#d1d5db');
  const getIntentColor = (i) => ({ Informational: '#3b82f6', Transactional: '#10b981', Navigational: '#8b5cf6', Comparison: '#f97316' }[i] || '#d1d5db');
  const getOpportunityColor = (o) => ({ High: '#10b981', Medium: '#eab308', Low: '#d1d5db' }[o] || '#d1d5db');

  // Score gauge
  const renderScoreGauge = () => {
    const score = data.questionScore || 0;
    const circumference = 2 * Math.PI * 60;
    const offset = circumference - (score / 100) * circumference;
    const color = score >= 70 ? '#10b981' : score >= 40 ? '#eab308' : '#ef4444';
    return (
      <svg viewBox="0 0 150 150" style={{ width: 150, height: 150, display: 'block', margin: '0 auto' }}>
        <circle cx="75" cy="75" r="60" fill="none" stroke="#1f2937" strokeWidth="10" />
        <circle cx="75" cy="75" r="60" fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90, 75, 75)" style={{ transition: 'stroke-dashoffset 1.5s ease-out' }} />
        <text x="75" y="68" textAnchor="middle" fill="#fff" fontSize="26" fontWeight="bold">{score}</text>
        <text x="75" y="88" textAnchor="middle" fill="#d1d5db" fontSize="11">/ 100</text>
      </svg>
    );
  };

  // Category donut chart
  const renderCategoryDonut = () => {
    const cats = data.categories || [];
    if (cats.length === 0) return null;
    const total = cats.reduce((s, c) => s + c.percentage, 0);
    let cumulative = 0;
    const radius = 60;
    const circumference = 2 * Math.PI * radius;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
        <svg viewBox="0 0 150 150" style={{ width: 140, height: 140 }}>
          {cats.map((c, i) => {
            const dashLength = (c.percentage / total) * circumference;
            const dashOffset = -(cumulative / total) * circumference;
            cumulative += c.percentage;
            return (
              <circle key={i} cx="75" cy="75" r={radius} fill="none" stroke={c.color} strokeWidth="16"
                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                strokeDashoffset={dashOffset} transform="rotate(-90, 75, 75)" />
            );
          })}
          <text x="75" y="72" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="bold">{data.totalQuestions || 0}</text>
          <text x="75" y="86" textAnchor="middle" fill="#d1d5db" fontSize="9">questions</text>
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {cats.map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
              <span style={{ color: '#d1d5db', fontSize: 15 }}>{c.name} ({c.count})</span>
              <span style={{ color: '#d1d5db', fontSize: 14 }}>{c.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // AI platform optimization bars
  const renderPlatformBars = () => {
    const platforms = data.aiPlatformQuestions || [];
    if (platforms.length === 0) return null;
    const colors = { ChatGPT: '#10b981', 'Google AI': '#3b82f6', Perplexity: '#8b5cf6', Claude: '#f97316', 'Bing Chat': '#06b6d4' };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {platforms.map((p, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#d1d5db', fontSize: 16, fontWeight: 600 }}>{p.platform}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: getPriorityColor(p.brandMentionLikelihood === 'High' ? 'Low' : p.brandMentionLikelihood === 'Low' ? 'High' : 'Medium'), fontSize: 14 }}>
                  {p.brandMentionLikelihood} mention
                </span>
                <span style={{ color: colors[p.platform] || '#6366f1', fontSize: 16, fontWeight: 600 }}>{p.optimizationScore}%</span>
              </div>
            </div>
            <div style={{ background: '#1f2937', borderRadius: 4, height: 8, overflow: 'hidden' }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${p.optimizationScore}%` }} transition={{ duration: 1, delay: i * 0.15 }}
                style={{ height: '100%', borderRadius: 4, background: colors[p.platform] || '#6366f1' }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'questions', label: 'Questions & Gaps' },
    { id: 'platforms', label: 'AI Platforms & Strategy' }
  ];

  return (
    <div style={{ color: '#fff' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 600,
              background: activeTab === tab.id ? '#6366f1' : '#1f2937', color: activeTab === tab.id ? '#fff' : '#d1d5db', transition: 'all 0.2s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Question Score</h4>
              {renderScoreGauge()}
            </div>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Key Metrics</h4>
              {[
                { label: 'Total Questions', value: data.totalQuestions || 0 },
                { label: 'Avg Monthly Searches', value: data.avgMonthlySearches || 0 },
                { label: 'Growth Trend', value: data.questionGrowth || 'N/A' }
              ].map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 2 ? '1px solid #1f2937' : 'none' }}>
                  <span style={{ color: '#d1d5db', fontSize: 16 }}>{m.label}</span>
                  <span style={{ color: '#6366f1', fontSize: 16, fontWeight: 600 }}>{typeof m.value === 'number' ? m.value.toLocaleString() : m.value}</span>
                </div>
              ))}
              <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ background: '#1f2937', color: '#6366f1', padding: '3px 8px', borderRadius: 10, fontSize: 13 }}>
                  {data.dataSources?.relatedKeywords || 0} keywords
                </span>
                <span style={{ background: '#1f2937', color: '#8b5cf6', padding: '3px 8px', borderRadius: 10, fontSize: 13 }}>
                  {data.dataSources?.suggestions || 0} suggestions
                </span>
                <span style={{ background: '#1f2937', color: '#10b981', padding: '3px 8px', borderRadius: 10, fontSize: 13 }}>
                  {data.dataSources?.serpResults || 0} SERP results
                </span>
              </div>
            </div>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Question Categories</h4>
              {renderCategoryDonut()}
            </div>
          </div>

          {/* Question Trends */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Question Trends</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
              {(data.questionTrends || []).map((t, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{t.trend}</span>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ color: getDirectionColor(t.direction), fontSize: 18, fontWeight: 700 }}>{getDirectionIcon(t.direction)}</span>
                      <span style={{ color: getDirectionColor(t.direction), fontSize: 14 }}>{t.direction}</span>
                    </div>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 6 }}>{t.description}</p>
                  <span style={{ color: getPriorityColor(t.impact), fontSize: 14, fontWeight: 600 }}>{t.impact} Impact</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Question Clusters */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Question Clusters</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {(data.questionClusters || []).map((cl, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{cl.cluster}</span>
                    <span style={{ background: '#1f2937', color: '#6366f1', padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
                      {cl.totalVolume?.toLocaleString()} vol
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{ background: getIntentColor(cl.dominantIntent) + '22', color: getIntentColor(cl.dominantIntent), padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>
                      {cl.dominantIntent}
                    </span>
                  </div>
                  {cl.questions?.map((q, j) => (
                    <p key={j} style={{ color: '#d1d5db', fontSize: 14, padding: '3px 0', borderBottom: j < cl.questions.length - 1 ? '1px solid #1f2937' : 'none' }}>
                       {q}
                    </p>
                  ))}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* QUESTIONS & GAPS TAB */}
      {activeTab === 'questions' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          {/* Top Questions Table */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24, overflowX: 'auto' }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Top Search Questions</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #374151' }}>
                  {['Question', 'Volume', 'Difficulty', 'Intent', 'AI Relevance', 'Status', 'Priority'].map(h => (
                    <th key={h} style={{ color: '#d1d5db', fontSize: 14, fontWeight: 600, padding: '8px 6px', textAlign: 'left', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.topQuestions || []).map((q, i) => (
                  <motion.tr key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}
                    style={{ borderBottom: '1px solid #1f2937' }}>
                    <td style={{ color: '#d1d5db', fontSize: 15, padding: '10px 6px', maxWidth: 250 }}>{q.question}</td>
                    <td style={{ color: '#6366f1', fontSize: 15, padding: '10px 6px', fontWeight: 600 }}>{q.volume?.toLocaleString()}</td>
                    <td style={{ padding: '10px 6px' }}>
                      <span style={{ color: getDifficultyColor(q.difficulty), fontSize: 14, fontWeight: 600 }}>{q.difficulty}</span>
                    </td>
                    <td style={{ padding: '10px 6px' }}>
                      <span style={{ background: getIntentColor(q.intent) + '22', color: getIntentColor(q.intent), padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>{q.intent}</span>
                    </td>
                    <td style={{ padding: '10px 6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 40, background: '#1f2937', borderRadius: 3, height: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${q.aiRelevance}%`, height: '100%', background: '#6366f1', borderRadius: 3 }} />
                        </div>
                        <span style={{ color: '#d1d5db', fontSize: 13 }}>{q.aiRelevance}%</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 6px' }}>
                      <span style={{ color: q.answered ? '#10b981' : '#ef4444', fontSize: 16 }}>{q.answered ? '' : ''}</span>
                    </td>
                    <td style={{ padding: '10px 6px' }}>
                      <span style={{ background: getPriorityColor(q.priority) + '22', color: getPriorityColor(q.priority), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{q.priority}</span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Content Gaps */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Content Gaps</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(data.contentGaps || []).map((g, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, borderLeft: `3px solid ${getOpportunityColor(g.opportunity)}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{g.question}</span>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <span style={{ background: '#1f2937', color: '#6366f1', padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
                        {g.searchVolume?.toLocaleString()} /mo
                      </span>
                      <span style={{ background: getOpportunityColor(g.opportunity) + '22', color: getOpportunityColor(g.opportunity), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
                        {g.opportunity}
                      </span>
                    </div>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15 }}> {g.suggestedContent}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* AI PLATFORMS & STRATEGY TAB */}
      {activeTab === 'platforms' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          {/* AI Platform Optimization */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>AI Platform Optimization</h4>
            {renderPlatformBars()}
          </div>

          {/* Platform Likely Questions */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Likely AI Platform Questions</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {(data.aiPlatformQuestions || []).map((p, i) => {
                const colors = { ChatGPT: '#10b981', 'Google AI': '#3b82f6', Perplexity: '#8b5cf6', Claude: '#f97316', 'Bing Chat': '#06b6d4' };
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                    style={{ background: '#0d1117', borderRadius: 10, padding: 14, borderTop: `3px solid ${colors[p.platform] || '#6366f1'}` }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16, marginBottom: 8, display: 'block' }}>{p.platform}</span>
                    {p.likelyQuestions?.map((q, j) => (
                      <p key={j} style={{ color: '#d1d5db', fontSize: 15, padding: '4px 0', borderBottom: j < p.likelyQuestions.length - 1 ? '1px solid #1f2937' : 'none' }}>
                        {j + 1}. {q}
                      </p>
                    ))}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Recommendations */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Recommendations</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(data.recommendations || []).map((rec, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 14, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{rec.title}</span>
                    <span style={{ background: getPriorityColor(rec.priority) + '22', color: getPriorityColor(rec.priority), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600, flexShrink: 0 }}>
                      {rec.priority}
                    </span>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 6 }}>{rec.description}</p>
                  <p style={{ color: '#6366f1', fontSize: 14, fontStyle: 'italic' }}>Expected: {rec.expectedImpact}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SearchQuestionsAnalysis;
